import { createTheme } from '@mui/material/styles';

const theme = createTheme({
  palette: {
    primary: {
      main: '#05BDFF',
      dark: '#04A0DB',
      light: '#E7F8FF',
      // hover: '#cee4ff',
    },
    secondary: {
      main: '#091115',
      light: '#F6F8FB',
    },
    text: {
      primary: '#091115',
      secondary: '#5B6B80',
      disabled: '#DEE2E6',
    },
    error: {
      main: '#E4002B',
      light: '#FFE7D9',
      dark: '#C92A2A',
    },
    warning: {
      main: '#F08C00',
      light: '#FFF3CC',
      dark: '#B14E06',
    },
    success: {
      main: '#29CC6A',
      light: '#C9F4E2',
      dark: '#1D6226',
    },
    info: {
      main: '#0099FF',
      light: '#F3F0FF',
      // dark: '#7048E8',
    },
    grey: {
      50: '#F1F3F5',
      100: '#F8F9FA',
      200: '#E9ECEF',
      300: '#D9D9D9',
      400: '#CFD4DA',
      500: '#ADB5BD',
      600: '#868E96',
      800: '#495057',
      900: '#343A40',
    },
  },
  shape: {
    borderRadius: 10,
  },
  typography: {
    fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
    h1: {
      fontSize: '32px',
      fontWeight: '700',
      fontFamily: 'Inter, sans-serif',
      letterSpacing: '-0.6px',
      color: '#091115',
    },
    h2: {
      fontSize: '26px',
      fontWeight: '700',
      fontFamily: 'Inter, sans-serif',
      letterSpacing: '-0.5px',
      color: '#091115',
    },
    h3: {
      fontSize: '20px',
      fontWeight: '600',
      fontFamily: 'Inter, sans-serif',
    },
    h4: {
      fontSize: '18px',
      fontWeight: '600',
      fontFamily: 'Inter, sans-serif',
    },
    h5: {
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Inter, sans-serif',
    },
    h6: {
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: 'Inter, sans-serif',
    },
    subtitle1: {
      fontSize: '18px',
      fontWeight: '600',
      fontFamily: 'Inter, sans-serif',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: '400',
      fontFamily: 'Inter, sans-serif',
    },
    body1: {
      fontSize: '16px',
      letterSpacing: '-0.42px',
      fontWeight: '400',
      fontFamily: 'Inter, sans-serif',
      lineHeight: '20px',
    },
    body2: {
      fontSize: '14px',
      letterSpacing: '-0.42px',
      fontWeight: '400',
      fontFamily: 'Inter, sans-serif',
      lineHeight: '18px',
    },
    button: {
      fontSize: '16px',
      fontWeight: '600',
      textTransform: 'initial',
      fontFamily: 'Inter, sans-serif',
    },
  },
  components: {
    // SKD-2140: an icon button is 40x40 by default, and on a touch screen it is very often the
    // only way to reach an action - the table row menus, the pagination arrows and the
    // navigation hamburger on every page are all icon buttons. Below md they take the 44x44
    // minimum the iOS and Android guidelines ask for. From md up nothing changes.
    MuiIconButton: {
      styleOverrides: {
        root: {
          '@media (max-width:899.95px)': {
            minWidth: '44px',
            minHeight: '44px',
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
          // same reasoning: a 40px-tall button is a small target on a phone
          '@media (max-width:899.95px)': {
            minHeight: '44px',
          },
        },
      },
      variants: [
        {
          props: { variant: 'text' },
          style: {
            fontWeight: '400',
          },
        },
        {
          props: { variant: 'contained' },
          style: {
            backgroundColor: '#05BDFF',
            color: '#fff',
            '&:hover': {
              backgroundColor: '#04A0DB',
            },
          },
        },
        {
          props: { size: 'large' },
          style: {
            padding: '10px 30px',
            fontSize: '14px',
          },
        },
      ],
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          backgroundColor: '#F6F8FB',
          boxShadow: 'none',
          '&.MuiInputBase-root': {
            borderRadius: '10px',
          },
          // Subtle 1px border to match the prototype's clean fields; cyan on focus.
          // SKD-2382: labels sit ABOVE outlined fields in this theme (MuiFormLabel below), so the
          // border's "notch" - a gap MUI cuts for a floating label - has nothing to hold and
          // shows as a white square over the top border when a field is focused or filled.
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(9, 17, 21, 0.10)',
            top: 0,
            '& legend': { display: 'none' },
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(9, 17, 21, 0.18)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#05BDFF',
            borderWidth: '1px',
          },
          '&.MuiInputBase-multiline': {
            '& .MuiOutlinedInput-input': {
              fontWeight: 400,
            },
          },
        },
        input: {
          borderRadius: '10px',
          border: 'none',
          color: '#091115',
          fontWeight: 400,
          fontSize: '15px',
          '&[readonly]': {
            fontWeight: 400,
            color: '#ADB5BD',
          },
          '&[disabled]': {
            color: '#ADB5BD',
          },
          '&::placeholder': {
            color: '#ADB5BD',
            opacity: 1,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-outlined': {
            position: 'relative',
            transform: 'none',
            color: '#868E96',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '-0.42px',
            paddingBottom: '4px',
            paddingLeft: '10px',
            '&.Mui-focused': {
              color: '#06BDFF',
            },
          },
        },
      },
    },
  },
});

export default theme;
